import PropTypes from "prop-types";
import React, { useState, useEffect, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Backdrop from "@material-ui/core/Backdrop";
import { ClipLoader } from "react-spinners";
import moment from "moment";
import ReactTable from "react-table";
import Button from "../../Button";
import Grid from "@material-ui/core/Grid";
import Snackbar from "../../Snackbar";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Checkbox from "@material-ui/core/Checkbox";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContentText from "@material-ui/core/DialogContentText";
import TextField from "@material-ui/core/TextField";
import {
  confirmationError,
  confirmationSuccess,
} from "../../../styles/common.css";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { FormControl } from "@material-ui/core/";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
  centeredTable: {
    "& .rt-th, & .rt-td": {
      textAlign: "center",
      justifyContent: "center",
      alignItems: "center",
      display: "flex",
    },
  },
  checkboxCell: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "45px",
  }
}));

const GeneratePayment = ({
  open,
  onClose,
  selectedTransactions,
  tdsps,
  fetchCreatePayment,
  tdsp,
}) => {
  const classes = useStyles();
  const [tdspName, setTdspName] = useState(tdsp);
  const [paymentMethod, setPaymentMethod] = useState("ACH");
  const [settlementDate, setSettlementDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [traceNumber, setTraceNumber] = useState("");

  const totalAmount = useMemo(() => {
    if (!selectedTransactions?.length) return 0;
    return selectedTransactions.reduce((sum, txn) => {
      const val = parseFloat(txn.invoice_total || 0);
      return sum + (isNaN(val) ? 0 : val);
    }, 0);
  }, [selectedTransactions]);

  const isFormValid =
    tdspName &&
    paymentMethod &&
    settlementDate &&
    traceNumber.trim() !== "" &&
    selectedTransactions?.length > 0 &&
    new Date(settlementDate) >= new Date(new Date().toISOString().split("T")[0]);

  const handleSave = async () => {
    if (!isFormValid) return;

    try {
      await fetchCreatePayment({
        duns_number: tdspName,
        total_amount: totalAmount,
        payment_method: paymentMethod,
        settlement_date: settlementDate,
        trace_number: traceNumber,
        transactions: selectedTransactions.map((x) => x.id),
      });
      onClose();
    } catch (err) {
      console.error("Error creating payment:", err);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      contentlabel="Transaction Raw Data"
      ariaHideApp={false}
      fullWidth
      maxWidth="md"
      classes={{ paper: classes.dialog }}
    >
      <DialogTitle>Generate 820 Payment</DialogTitle>
      <DialogContent style={{ overflow: "hidden" }}>
        <DialogContentText>
          <Grid container spacing={2} className={classes.transactionBody}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="TDSP Names"
                fullWidth
                variant="outlined"
                value={tdspName}
                disabled
                onChange={(e) => setTdspName(e.target.value)}
                className={classes.inputField}
              >
                {tdsps.map((tdsp) => (
                  <MenuItem key={tdsp.duns_number} value={tdsp.duns_number}>
                    {tdsp.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Total Amount"
                fullWidth
                variant="outlined"
                value={totalAmount.toFixed(2)}
                InputProps={{ readOnly: true }}
                className={classes.inputField}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Payment Method"
                fullWidth
                variant="outlined"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className={classes.inputField}
              >
                {["ACH", "FEW", "FWT"].map((method) => (
                  <MenuItem key={method} value={method}>
                    {method}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Settlement Date"
                type="date"
                fullWidth
                variant="outlined"
                value={settlementDate}
                onChange={(e) => setSettlementDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                className={classes.inputField}
                inputProps={{
                  min: new Date().toISOString().split("T")[0],
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Trace Number"
                fullWidth
                variant="outlined"
                value={traceNumber}
                onChange={(e) => setTraceNumber(e.target.value.slice(0, 30))}
                inputProps={{ maxLength: 30 }}
                className={classes.inputField}
              />
            </Grid>
          </Grid>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleSave}
          color="secondary"
          variant="contained"
          style={{ marginRight: 10, marginBottom: 10 }}
          disabled={!isFormValid}
        >
          Save
        </Button>
        <Button
          onClick={onClose}
          color="primary"
          variant="contained"
          style={{ marginRight: 20, marginBottom: 10 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CreatePayments = ({
  unpaidTransactions,
  showSnackBar,
  successMessage,
  errorMessage,
  total,
  has_next,
  fetchAllUnpaidTransactions,
  fetchTdsps,
  closeSnackbar,
  isProcessing,
  tdsps,
  createPayment,
}) => {
  const [filter, setFilter] = useState({});
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [openGeneratePayment, setOpenGeneratePayment] = useState(false);
  const [openNoSelectionDialog, setOpenNoSelectionDialog] = useState(false);
  const [sortedTdsps, setSortedTdsps] = useState([]);
  const classes = useStyles();
  const localtz = moment.tz.guess();

  const fetchData = (page, filter = {}) => {
    if (filter && filter.tdsp) {
      fetchAllUnpaidTransactions(page, filter);
      setSelectedTransactions([]);
    }
  };

  useEffect(() => {
    const START_PAGE = 1;
    fetchTdsps();
    fetchData(START_PAGE);
  }, []);

  const handleGroupSelect = (checked, group) => {
    const txns = group.transactions || [];
    if (checked) {
      setSelectedTransactions((prev) => {
        const existingIds = new Set(prev.map((t) => t.id));
        const newOnes = txns.filter((t) => !existingIds.has(t.id));
        return [...prev, ...newOnes];
      });
    } else {
      setSelectedTransactions((prev) =>
        prev.filter((t) => !txns.some((x) => x.id === t.id))
      );
      setSelectAll(false);
    }
  };

  const columns = [
    {
      Header: (
        <div className={classes.checkboxCell}>
          <Checkbox
            checked={selectAll}
            onChange={(e) => {
              const checked = e.target.checked;
              setSelectAll(checked);
              if (checked) {
                const allSubTxns = unpaidTransactions.flatMap(
                  (t) => t.transactions || []
                );
                setSelectedTransactions(allSubTxns);
              } else {
                setSelectedTransactions([]);
              }
            }}
            color="primary"
          />
        </div>
      ),
      accessor: "checkbox",
      sortable: false,
      width: 40,
      Cell: ({ original }) => {
        const allTxns = original.transactions || [];
        const allSelected = allTxns.every((txn) =>
          selectedTransactions.map((x) => x.id).includes(txn.id)
        );
        return (
          <Checkbox
            checked={allSelected}
            indeterminate={
              !allSelected &&
              allTxns.some((txn) =>
                selectedTransactions.map((x) => x.id).includes(txn.id)
              )
            }
            onChange={(e) => handleGroupSelect(e.target.checked, original)}
            color="primary"
          />
        );
      },
    },
    {
      Header: "Due Date",
      accessor: "due_date",
      Cell: ({ value }) => (value ? moment(value).format("YYYY-MM-DD") : ""),
    },
    {
      Header: "Total Amount",
      accessor: "total_amount",
      Cell: ({ value }) => parseFloat(value || 0).toFixed(2),
    },
  ];

  const handleSelect = (e, type) => {
    if (e.target.value) setFilter({ ...filter, [type]: e.target.value });
    else setFilter({ ...filter, [type]: null });
  };

  const handlePicker = (e, type) => {
    if (e) setFilter({ ...filter, [type]: moment(e).format("YYYY-MM-DD") });
    else setFilter({ ...filter, [type]: null });
  };

  const handleConfirm = (e) => {
    e.preventDefault();
    const cleanFilter = {};
    Object.keys(filter).map((x) => {
      if (filter[x]) cleanFilter[x] = filter[x];
    });
    const START_PAGE = 1;
    fetchData(START_PAGE, cleanFilter);
  };

  const handleGeneratePayment = () => {
    if (selectedTransactions.length === 0) {
      setOpenNoSelectionDialog(true);
      return;
    } else {
      setOpenGeneratePayment(true);
    }
  };

  const fetchCreatePayment = (data) => {
    return createPayment(data).then(() => {
      const cleanFilter = {};
      Object.keys(filter).map((x) => {
        if (filter[x]) cleanFilter[x] = filter[x];
      });
      const START_PAGE = 1;
      setSelectedTransactions([]);
      fetchData(START_PAGE, cleanFilter);
    });
  };

  useEffect(() => {
    if (tdsps && tdsps.length > 0) {
      const sorted = [...tdsps].sort((a, b) => a.name.localeCompare(b.name));
      setSortedTdsps(sorted);
      setFilter((prev) => ({
        ...prev,
        tdsp: prev.tdsp || sorted[0]?.duns_number || "",
      }));
    }
  }, [tdsps]);

  return (
    <div>
      <Grid container>
        <Grid item md={12}>
          <FormControl
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-around",
            }}
          >
            <Grid container>
              <Grid
                item
                xs={2}
                style={{ marginRight: "30px", marginBottom: "20px" }}
              >
                <div style={{ marginBottom: "-5px" }}>
                  <label>From Due Date:</label>
                </div>
                <KeyboardDatePicker
                  onChange={(e) => handlePicker(e, "from_date")}
                  dateformat="YYYY-MM-DD"
                  value={filter.from_date ? moment(filter.from_date).tz(localtz) : null}
                  views={["year", "month", "date"]}
                  style={{ width: "100%" }}
                />
              </Grid>

              <Grid
                item
                xs={2}
                style={{ marginRight: "30px", marginBottom: "20px" }}
              >
                <div style={{ marginBottom: "-5px" }}>
                  <label>To Due Date:</label>
                </div>
                <KeyboardDatePicker
                  onChange={(e) => handlePicker(e, "to_date")}
                  dateformat="YYYY-MM-DD"
                  value={filter.to_date ? moment(filter.to_date).tz(localtz) : null}
                  views={["year", "month", "date"]}
                  style={{ width: "100%" }}
                />
              </Grid>

              <Grid
                item
                xs={2}
                style={{ marginRight: "30px", marginBottom: "20px" }}
              >
                <div style={{ marginBottom: "-1px" }}>
                  <label>TDSP</label>
                </div>
                <Select
                  id="transaction_type"
                  style={{ width: "100%", marginTop: "-20px" }}
                  onChange={(e) => handleSelect(e, "tdsp")}
                  value={filter.tdsp ? filter.tdsp : ""}
                >
                  {sortedTdsps &&
                    sortedTdsps.map((a) => (
                      <MenuItem key={a.duns_number} value={a.duns_number}>
                        {a.name}
                      </MenuItem>
                    ))}
                </Select>
              </Grid>

              <Grid
                item
                xs={3}
                style={{ marginRight: "30px", marginBottom: "20px" }}
              >
                <div>
                  <Button
                    onClick={handleConfirm}
                    color="primary"
                    variant="contained"
                    style={{ marginBottom: "30px" }}
                  >
                    Search
                  </Button>
                  <Button
                    onClick={handleGeneratePayment}
                    color="secondary"
                    variant="contained"
                    style={{ marginBottom: "30px", marginLeft: "10px" }}
                  >
                    GENERATE PAYMENT
                  </Button>
                </div>
              </Grid>
            </Grid>
          </FormControl>
        </Grid>
      </Grid>

      <ReactTable
        data={unpaidTransactions || []}
        columns={columns}
        className={`-striped -highlight ${classes.centeredTable}`}
        defaultPageSize={10}
        SubComponent={({ original }) => (
          <div style={{ padding: "10px 20px" }}>
            <ReactTable
              data={original.transactions || []}
              columns={[
                { Header: "Invoice #", accessor: "invoice_number" },
                { Header: "Cross Ref #", accessor: "cross_reference_number" },
                { Header: "ESI ID", accessor: "esi_id" },
                { Header: "Invoice Type", accessor: "invoice_type" },
                {
                  Header: "Invoice Total",
                  accessor: "invoice_total",
                  Cell: ({ value }) => parseFloat(value || 0).toFixed(2),
                },
              ]}
              showPagination={false}
              minRows={1}
              className="-highlight"
            />
          </div>
        )}
      />

      <Snackbar
        id="message-id"
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        open={showSnackBar}
        onClose={closeSnackbar}
        message={successMessage || errorMessage}
        className={successMessage ? confirmationSuccess : confirmationError}
      />

      {openGeneratePayment && (
        <GeneratePayment
          open={openGeneratePayment}
          onClose={() => setOpenGeneratePayment(false)}
          selectedTransactions={selectedTransactions}
          tdsps={sortedTdsps}
          fetchCreatePayment={fetchCreatePayment}
          tdsp={filter.tdsp}
        />
      )}

      <Dialog
        open={openNoSelectionDialog}
        onClose={() => setOpenNoSelectionDialog(false)}
      >
        <DialogTitle>Warning</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please select at least one transaction before generating a payment.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenNoSelectionDialog(false)}
            color="primary"
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {has_next && (
        <Button
          onClick={async () => {
            await fetchData();
          }}
          color="primary"
          variant="contained"
          style={{
            marginRight: 20,
            marginTop: 10,
          }}
        >
          Load More
        </Button>
      )}

      <div style={{ float: "right", marginTop: "10px" }}>
        Downloaded {unpaidTransactions.length} of {total} unpaid transactions
      </div>

      <Backdrop className={classes.backdrop} open={isProcessing}>
        <ClipLoader sizeUnit="px" size={100} color="#ED0874" loading />
      </Backdrop>
    </div>
  );
};

CreatePayments.propTypes = {
  closeSnackbar: PropTypes.any,
  errorMessage: PropTypes.any,
  fetchAllUnpaidTransactions: PropTypes.func,
  fetchMeterTransactions: PropTypes.func,
  params: PropTypes.shape({
    memberId: PropTypes.any,
  }),
  showSnackBar: PropTypes.any,
  successMessage: PropTypes.any,
  unpaidTransactions: PropTypes.array,
  total: PropTypes.number,
  has_next: PropTypes.bool,
  tdsps: PropTypes.array,
  fetchTdsps: PropTypes.func,
  isProcessing: PropTypes.bool,
  location: PropTypes.object,
};

export default CreatePayments;
